// Copyright (c) Ping. All rights reserved.
//
// Web video tile — a full inline media player built over a raw <video>
// element with a custom control bar: play/pause, scrubber, elapsed /
// total time, mute, Picture-in-Picture, fullscreen and download. Before
// the host resolves the decrypted object URL (`videoUri`) the card shows
// the poster + a center play disc; tapping it calls `onTapOpen` so the
// host can kick off the fetch/decrypt, then the player swaps in when
// `videoUri` arrives.
//
// Geometry matches `ImageAttachmentCard` (300-wide wrap, radius 16, 2 px
// inset) so video + image tiles align in the timeline. Control chrome
// uses NativeWind classes (real Tailwind CSS on web); the raw <video> and
// <input range> are DOM nodes styled inline (a class genuinely can't
// drive their media-specific layout reliably across the dual-RN setup —
// the documented fallback, same as ImageAttachmentCard's <Image>).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import {
  Download,
  Info,
  Loader2,
  Maximize2,
  Pause,
  PictureInPicture2,
  Play,
  RotateCw,
  Video,
  Volume2,
  VolumeX,
} from 'lucide-react-native';
import { tokens } from '../../design-system/tokens';
import { clampAspectRatio } from '../messageFormatters';
import type { VideoAttachmentCardProps } from './VideoAttachmentCard.types';
import { ReadReceiptOverlay } from '../ReadReceiptOverlay';
import { useTileMaxWidth } from '../tileConstraint';

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? '0' : ''}${r}`;
}

export const VideoAttachmentCard = React.memo<VideoAttachmentCardProps>(
  ({
    state,
    aspectRatio,
    posterUri,
    videoUri,
    durationLabel,
    caption,
    progress,
    downloading,
    downloadProgress,
    isSelf,
    timeLabel,
    delivered,
    read,
    isFirstOfGroup,
    isLastOfGroup,
    onTapOpen,
    onDownload,
    onFullscreen,
    onRetry,
    testID,
    readers,
  }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];
    // Thread side panel: cap the tile to the provided width.
    const tileMax = useTileMaxWidth();

    const ratio = clampAspectRatio(aspectRatio);
    const isFailed = state === 'failed';
    const isUploading = state === 'uploading';
    const wrapBackground = isSelf ? colors.accent.brand : colors.surface.elevated;

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const wantPlayRef = useRef(false);
    const [started, setStarted] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [cur, setCur] = useState(0);
    const [dur, setDur] = useState(0);
    const [hover, setHover] = useState(false);
    // Broken/expired clip URL — flip to a small inline error instead of a
    // silent black frame. Reset when a fresh (e.g. re-signed) URL arrives.
    const [videoError, setVideoError] = useState(false);
    useEffect(() => {
      setVideoError(false);
    }, [videoUri]);

    // Reflect the element's own events back into React state (covers
    // user-driven changes via PiP / native fullscreen controls too).
    // CRUCIAL: this must re-run once the <video> actually MOUNTS (it only
    // mounts when `started` flips true), otherwise `videoRef.current` is
    // null at first run and no listeners ever attach — leaving the icons
    // frozen and the scrubber stuck at 0.
    useEffect(() => {
      const v = videoRef.current;
      if (v === null) return;
      const onPlay = (): void => setPlaying(true);
      const onPause = (): void => setPlaying(false);
      const onTime = (): void => setCur(v.currentTime);
      const onMeta = (): void => setDur(v.duration);
      const onVol = (): void => setMuted(v.muted);
      const onEnd = (): void => setPlaying(false);
      v.addEventListener('play', onPlay);
      v.addEventListener('pause', onPause);
      v.addEventListener('timeupdate', onTime);
      v.addEventListener('loadedmetadata', onMeta);
      v.addEventListener('durationchange', onMeta);
      v.addEventListener('volumechange', onVol);
      v.addEventListener('ended', onEnd);
      // Sync whatever state the element already has at attach time.
      setPlaying(!v.paused);
      setMuted(v.muted);
      if (Number.isFinite(v.duration)) setDur(v.duration);
      setCur(v.currentTime);
      return () => {
        v.removeEventListener('play', onPlay);
        v.removeEventListener('pause', onPause);
        v.removeEventListener('timeupdate', onTime);
        v.removeEventListener('loadedmetadata', onMeta);
        v.removeEventListener('durationchange', onMeta);
        v.removeEventListener('volumechange', onVol);
        v.removeEventListener('ended', onEnd);
      };
    }, [videoUri, started]);

    const togglePlay = useCallback((): void => {
      const v = videoRef.current;
      if (v === null) return;
      if (v.paused) void v.play().catch(() => undefined);
      else v.pause();
    }, []);

    const onCenterPress = useCallback((): void => {
      if (videoUri === undefined) {
        // Not resolved yet — let the host begin fetch/decrypt, and
        // remember to auto-start once the URL arrives (one-tap UX).
        wantPlayRef.current = true;
        onTapOpen();
        return;
      }
      setStarted(true);
      // Defer one tick so the <video> mounts before we play it.
      requestAnimationFrame(() => togglePlay());
    }, [videoUri, onTapOpen, togglePlay]);

    // Auto-start when a lazily-resolved videoUri lands after the user
    // already tapped play on the poster.
    useEffect(() => {
      if (videoUri !== undefined && wantPlayRef.current && !started) {
        wantPlayRef.current = false;
        setStarted(true);
        requestAnimationFrame(() => togglePlay());
      }
    }, [videoUri, started, togglePlay]);

    const toggleMute = useCallback((): void => {
      const v = videoRef.current;
      if (v === null) return;
      v.muted = !v.muted;
    }, []);

    const enterPip = useCallback((): void => {
      const v = videoRef.current as
        | (HTMLVideoElement & { requestPictureInPicture?: () => Promise<unknown> })
        | null;
      if (v?.requestPictureInPicture !== undefined) {
        void v.requestPictureInPicture().catch(() => undefined);
      }
    }, []);

    const enterFullscreen = useCallback((): void => {
      // Host-driven fullscreen → open the shared MediaPreviewScreen (keeps
      // this inline player intact). Falls back to the browser's native
      // fullscreen when no host handler is wired.
      if (onFullscreen !== undefined) {
        onFullscreen();
        return;
      }
      const v = videoRef.current as
        | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
        | null;
      if (v === null) return;
      if (typeof v.requestFullscreen === 'function') void v.requestFullscreen().catch(() => undefined);
      else if (typeof v.webkitEnterFullscreen === 'function') v.webkitEnterFullscreen();
    }, [onFullscreen]);

    const download = useCallback((): void => {
      if (onDownload !== undefined) {
        onDownload();
        return;
      }
      if (videoUri === undefined || typeof document === 'undefined') return;
      const a = document.createElement('a');
      a.href = videoUri;
      a.download = 'video';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }, [onDownload, videoUri]);

    const seek = useCallback((value: number): void => {
      const v = videoRef.current;
      if (v === null) return;
      v.currentTime = value;
      setCur(value);
    }, []);

    const pipSupported =
      typeof document !== 'undefined' &&
      (document as Document & { pictureInPictureEnabled?: boolean })
        .pictureInPictureEnabled === true;

    const showPlayer = started && videoUri !== undefined && !isFailed && !isUploading;
    const controlsVisible = hover || !playing;
    // Grouped-run blended corners (tail side flattens), mirroring
    // ImageAttachmentCard. Standalone tiles keep the full radius classes.
    const grouped = isFirstOfGroup !== undefined || isLastOfGroup !== undefined;
    const FULL = 16;
    const TIGHT = 6;
    const tailTop = isFirstOfGroup !== false ? FULL : TIGHT;
    const tailBottom = isLastOfGroup !== false ? FULL : TIGHT;
    const wrapCorners = !grouped
      ? {}
      : isSelf
        ? {
            borderTopLeftRadius: FULL,
            borderBottomLeftRadius: FULL,
            borderTopRightRadius: tailTop,
            borderBottomRightRadius: tailBottom,
          }
        : {
            borderTopRightRadius: FULL,
            borderBottomRightRadius: FULL,
            borderTopLeftRadius: tailTop,
            borderBottomLeftRadius: tailBottom,
          };
    const innerCorners = !grouped
      ? {}
      : Object.fromEntries(
          Object.entries(wrapCorners).map(([k, v]) => [k, Math.max(0, (v as number) - 2)]),
        );

    return (
      <View
        className="gap-xs w-[300px] max-w-full"
        style={tileMax !== null ? { maxWidth: tileMax } : undefined}
      >
        <View
          className="rounded-[16px] p-0.5"
          style={{ backgroundColor: wrapBackground, ...wrapCorners }}
          // @ts-expect-error react-native-web forwards DOM mouse handlers.
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <View
            className="rounded-[14px] overflow-hidden relative bg-[#15171C]"
            style={{ aspectRatio: ratio, ...innerCorners }}
            testID={testID}
          >
            {showPlayer ? (
              <>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  ref={videoRef}
                  src={videoUri}
                  poster={posterUri}
                  playsInline
                  preload="metadata"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    background: '#000',
                  }}
                  onClick={togglePlay}
                  onError={() => setVideoError(true)}
                />
                {videoError ? (
                  <View className="absolute inset-0 items-center justify-center bg-[#15171C]">
                    <Text className="font-sans text-[13px] leading-[17px] text-white/70 text-center px-4">
                      Couldn't load video
                    </Text>
                  </View>
                ) : null}
                {/* Control bar */}
                <View
                  className="absolute inset-x-0 bottom-0 px-2 pb-1.5 pt-6"
                  pointerEvents="box-none"
                  style={{ opacity: controlsVisible ? 1 : 0 }}
                >
                  {/* Gradient scrim (web-only CSS → raw div). */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      background:
                        'linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0))',
                    }}
                  />
                  {/* Scrubber */}
                  <input
                    type="range"
                    min={0}
                    max={dur > 0 ? dur : 0}
                    step={0.1}
                    value={Math.min(cur, dur || 0)}
                    onChange={(e: { target: { value: string } }) =>
                      seek(parseFloat(e.target.value))
                    }
                    style={{
                      width: '100%',
                      height: 4,
                      accentColor: tokens.color.semantic[colorScheme].accent.brand,
                      cursor: 'pointer',
                    }}
                  />
                  <View className="flex flex-row items-center gap-sm pt-1">
                    <Pressable accessibilityRole="button" accessibilityLabel={playing ? 'Pause' : 'Play'} onPress={togglePlay} hitSlop={6}>
                      {playing ? (
                        <Pause size={18} color="#FFFFFF" fill="#FFFFFF" />
                      ) : (
                        <Play size={18} color="#FFFFFF" fill="#FFFFFF" />
                      )}
                    </Pressable>
                    <Text className="font-sans text-[11px] leading-[14px] text-white tabular-nums">
                      {fmt(cur)} / {fmt(dur)}
                    </Text>
                    <View className="grow" />
                    <Pressable accessibilityRole="button" accessibilityLabel={muted ? 'Unmute' : 'Mute'} onPress={toggleMute} hitSlop={6}>
                      {muted ? <VolumeX size={17} color="#FFFFFF" /> : <Volume2 size={17} color="#FFFFFF" />}
                    </Pressable>
                    {pipSupported ? (
                      <Pressable accessibilityRole="button" accessibilityLabel="Picture in picture" onPress={enterPip} hitSlop={6}>
                        <PictureInPicture2 size={17} color="#FFFFFF" />
                      </Pressable>
                    ) : null}
                    <Pressable accessibilityRole="button" accessibilityLabel="Download video" onPress={download} hitSlop={6}>
                      <Download size={17} color="#FFFFFF" />
                    </Pressable>
                    <Pressable accessibilityRole="button" accessibilityLabel="Fullscreen" onPress={enterFullscreen} hitSlop={6}>
                      <Maximize2 size={17} color="#FFFFFF" />
                    </Pressable>
                  </View>
                </View>
              </>
            ) : (
              // Poster + center play disc (pre-play / pre-resolve).
              <Pressable
                className="absolute inset-0 items-center justify-center"
                accessibilityRole="button"
                accessibilityLabel={isFailed ? 'Video failed' : 'Play video'}
                onPress={isFailed ? onRetry ?? onTapOpen : onCenterPress}
              >
                {posterUri !== undefined ? (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `url(${posterUri})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                ) : videoUri !== undefined ? (
                  // No decoded poster (e.g. canvas capture failed) but we
                  // DO have the clip — show its first frame as the
                  // thumbnail (the sender always has the local blob).
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video
                    src={videoUri}
                    muted
                    playsInline
                    preload="metadata"
                    tabIndex={-1}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      pointerEvents: 'none',
                      background: '#000',
                    }}
                  />
                ) : null}
                {isUploading ? (
                  <View className="absolute inset-0 items-center justify-center bg-black/45">
                    <Text className="font-sans text-[16px] font-bold text-white">
                      {progress !== undefined ? `${Math.round(progress * 100)}%` : 'Uploading'}
                    </Text>
                  </View>
                ) : isFailed ? (
                  <View className="absolute inset-0 items-center justify-center bg-black/55">
                    <View className="flex flex-row items-center gap-sm rounded-full bg-black/55 px-4 py-2.5">
                      <RotateCw size={18} strokeWidth={2.5} color="#F5F6F8" />
                      <Text className="font-sans text-[16px] font-bold text-white">
                        {!isSelf ? 'Retry' : 'Resend video'}
                      </Text>
                    </View>
                  </View>
                ) : downloading ? (
                  // Lazy blob fetch in flight after a play tap → spinner
                  // (+ % when the size is known).
                  <View className="absolute inset-0 items-center justify-center gap-2 bg-black/45">
                    <View className="animate-spin">
                      <Loader2 size={30} color="#FFFFFF" />
                    </View>
                    {downloadProgress !== undefined && downloadProgress > 0 ? (
                      <Text className="font-sans text-[12px] leading-[16px] text-white">
                        {Math.round(downloadProgress * 100)}%
                      </Text>
                    ) : null}
                  </View>
                ) : (
                  <>
                    <View className="items-center justify-center rounded-full w-[42px] h-[42px] bg-[#E8EAED]">
                      <Play size={20} color="#4B515C" fill="#4B515C" />
                    </View>
                    {/* Bottom-left camera + duration — Figma. */}
                    {durationLabel !== undefined ? (
                      <View className="absolute bottom-2 left-2 flex flex-row items-center gap-1 rounded-full bg-black/45 px-1.5 py-0.5">
                        <Video size={12} color="#FFFFFF" />
                        <Text className="font-sans text-[10px] leading-[14px] text-white">
                          {durationLabel}
                        </Text>
                      </View>
                    ) : null}
                  </>
                )}
              </Pressable>
            )}
            {/* Time+tick: sibling to the Pressable inside the relative frame
                so pointer events are captured without a nested button. */}
            {!showPlayer && timeLabel !== undefined && !isUploading && !isFailed ? (
              <ReadReceiptOverlay
                timeLabel={timeLabel}
                {...(delivered !== undefined ? { delivered } : {})}
                {...(read !== undefined ? { read } : {})}
                isSelf={isSelf === true}
                {...(readers !== undefined ? { readers } : {})}
                style={{ position: 'absolute', bottom: 8, right: 8 }}
              />
            ) : null}
          </View>
          {/* Caption lives INSIDE the colored bubble wrap so the wrap (and
              its speech tail) encloses text + media as one unit — a caption
              outside the wrap left the tail floating detached. */}
          {caption !== undefined && caption.length > 0 ? (
            <Text
              className="font-sans text-[14px] leading-[19px] px-2 pt-1 pb-0.5"
              style={{ color: isSelf ? colors.text.onAccent : colors.text.primary }}
            >
              {caption}
            </Text>
          ) : null}
        </View>
        {isFailed ? (
          <View className="flex flex-row items-center gap-1 px-xs">
            <Text className="font-sans text-[10px] leading-[14px] text-[#FF5353]">
              {!isSelf ? 'Error downloading video.' : 'Error sending video message.'}
            </Text>
            <Info size={10} color="#FF5353" />
          </View>
        ) : null}
      </View>
    );
  },
);

VideoAttachmentCard.displayName = 'VideoAttachmentCard';
